import React from 'react';
import { Table, Form } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import Select from 'react-select';
import { postRequestOptions, getRequestOptions } from "../helpers/Fetchwrapper";
import { Card } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { numberFormat } from '../helpers/GlobalHelper';

interface WarehouseInfo {
    id_warehouses: number;
    name: string;
}

interface WarehouseStockData {
    id_warehouses: number;
    opening_stock: number;
    production: number;
    return: number;
    warehouse_transfer_in: number;
    shipment: number;
    warehouse_transfer_out: number;
    closing_stock: number;
}

interface ProductStockRow {
    serial_no: number;
    id_products: number;
    product_name: string;
    pack_size: string;
    warehouses: WarehouseStockData[];
    total_closing_stock: number;
}

interface CategoryStockGroup {
    id_product_categories: number;
    category_name: string;
    products: ProductStockRow[];
}

interface ReportResponse {
    warehouses: WarehouseInfo[];
    categories: CategoryStockGroup[];
}

interface WarehouseStockReportProps {}

const WarehouseStockReport: React.FC<WarehouseStockReportProps> = () => {

    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState<ReportResponse>({ warehouses: [], categories: [] });
    const [formData, setFormData] = useState({
        idProjects: '',
        idProductCategories: '',
        fromDate: '',
        toDate: '',
        showZeroQuantity: false,
    });

    const [projects, setProjects] = useState<any[]>([]);
    const [project, setProject] = useState({ label: 'Select Project', value: '' });

    const [productCategories, setProductCategories] = useState<{ label: string; value: string }[]>([]);
    const [productCategory, setProductCategory] = useState<{ label: string; value: string }>({ label: 'All Product Categories', value: '' });

    useEffect(() => {
        getProjects();
    }, []);

    useEffect(() => {
        if (projects.length > 0) {
            setProject(projects[0]);
            setFormData((prevFormData) => ({
                ...prevFormData,
                idProjects: projects[0].value,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects]);

    useEffect(() => {
        if (formData.idProjects) {
            getProductCategories(formData.idProjects);
        } else {
            setProductCategories([{ label: 'All Product Categories', value: '' }]);
            setProductCategory({ label: 'All Product Categories', value: '' });
        }
    }, [formData.idProjects]);

    const getProjects = () => {
        fetch(
            process.env.REACT_APP_API_URL + 'lead/projects',
            getRequestOptions()
        )
            .then((resp) => resp.json())
            .then((resp) => {
                const projectObj = resp.data.map((item: any) => ({
                    ...item,
                    label: item.name,
                    value: item.id,
                }));
                setProjects(projectObj);
            })
            .catch((error) => {
                console.log(error, 'catch the hoop');
            });
    };

    const getProductCategories = async (projectId: string) => {
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + 'product-categories?id_projects=' + projectId,
                getRequestOptions()
            );
            const data = await response.json();

            if (data && Array.isArray(data)) {
                const categoryOptions = data.map((item: any) => ({
                    label: item.category_name,
                    value: item.id_product_categories,
                }));

                const allOption = { label: 'All Product Categories', value: '' };
                setProductCategories([allOption, ...categoryOptions]);
                setProductCategory(allOption);
                setFormData((prev) => ({ ...prev, idProductCategories: '' }));
            }
        } catch (error) {
            console.error('Error fetching product categories:', error);
        }
    };

    const handleProjectChange = (selectedProject: any) => {
        setProject(selectedProject);
        if (selectedProject) {
            setFormData({ ...formData, idProjects: selectedProject.value, idProductCategories: '' });
        } else {
            setFormData({ ...formData, idProjects: '', idProductCategories: '' });
        }
    };

    const handleProductCategoryChange = (selectedCategory: any) => {
        setProductCategory(selectedCategory);
        if (selectedCategory) {
            setFormData({ ...formData, idProductCategories: selectedCategory.value });
        } else {
            setFormData({ ...formData, idProductCategories: '' });
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = event.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleViewReport = async () => {
        if (!formData.idProjects) {
            toast.error('Please select a project', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (!formData.fromDate || !formData.toDate) {
            toast.error('Please select both From Date and To Date', { position: 'bottom-right', autoClose: 4000 });
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + 'reports/warehouse-stock-report',
                postRequestOptions(formData)
            );
            const data = await response.json();

            if (data.success) {
                setReportData(data.data || { warehouses: [], categories: [] });
            } else {
                setReportData({ warehouses: [], categories: [] });
                if (data.errors && Array.isArray(data.errors)) {
                    data.errors.forEach((error: string) => toast.error(error, { position: 'bottom-right', autoClose: 4000 }));
                } else {
                    toast.error('Failed to load report', { position: 'bottom-right', autoClose: 4000 });
                }
            }
        } catch (error) {
            console.error('Error submitting data:', error);
            toast.error('Error loading report data', { position: 'bottom-right', autoClose: 4000 });
        } finally {
            setLoading(false);
        }
    };

    const getWarehouseData = (product: ProductStockRow, warehouseId: number): WarehouseStockData | undefined => {
        return product.warehouses.find((wh) => wh.id_warehouses === warehouseId);
    };

    const stickyGroupHeaderStyle: React.CSSProperties = {
        position: 'sticky',
        top: 0,
        zIndex: 1022,
        backgroundColor: '#f8f9fa',
        boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.4)',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
    };

    const stickySubHeaderStyle: React.CSSProperties = {
        position: 'sticky',
        top: 38,
        zIndex: 1021,
        backgroundColor: '#f8f9fa',
        boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.4)',
        whiteSpace: 'nowrap',
    };

    const stockInStyle: React.CSSProperties = { color: '#198754' };
    const stockOutStyle: React.CSSProperties = { color: '#dc3545' };
    const closingStockStyle: React.CSSProperties = { color: '#0d6efd' };

    const stockInHeaderStyle: React.CSSProperties = { ...stickySubHeaderStyle, ...stockInStyle };
    const stockOutHeaderStyle: React.CSSProperties = { ...stickySubHeaderStyle, ...stockOutStyle };
    const closingStockHeaderStyle: React.CSSProperties = { ...stickySubHeaderStyle, ...closingStockStyle };
    const totalClosingStockHeaderStyle: React.CSSProperties = { ...stickyGroupHeaderStyle, ...closingStockStyle };

    const warehouseColumnCount = 7;
    const totalColumns = 3 + (reportData.warehouses.length * warehouseColumnCount) + 1;
    const hasReportData = reportData.categories.some((category) => category.products.length > 0);

    const renderProductRow = (product: ProductStockRow) => (
        <tr key={product.id_products}>
            <td className='text-center'>{product.serial_no}</td>
            <td className='ps-4'>{product.product_name}</td>
            <td>{product.pack_size}</td>
            {reportData.warehouses.map((warehouse) => {
                const whData = getWarehouseData(product, warehouse.id_warehouses);
                return (
                    <React.Fragment key={`${product.id_products}-${warehouse.id_warehouses}`}>
                        <td className='text-end' style={stockInStyle}>{numberFormat(whData?.opening_stock ?? 0)}</td>
                        <td className='text-end' style={stockInStyle}>{numberFormat(whData?.production ?? 0)}</td>
                        <td className='text-end' style={stockInStyle}>{numberFormat(whData?.return ?? 0)}</td>
                        <td className='text-end' style={stockInStyle}>{numberFormat(whData?.warehouse_transfer_in ?? 0)}</td>
                        <td className='text-end' style={stockOutStyle}>{numberFormat(whData?.shipment ?? 0)}</td>
                        <td className='text-end' style={stockOutStyle}>{numberFormat(whData?.warehouse_transfer_out ?? 0)}</td>
                        <td className='text-end fw-bold' style={closingStockStyle}>{numberFormat(whData?.closing_stock ?? 0)}</td>
                    </React.Fragment>
                );
            })}
            <td className='text-end fw-bold' style={closingStockStyle}>{numberFormat(product.total_closing_stock ?? 0)}</td>
        </tr>
    );

    return (
        <>
            <Card className='mb-4'>
                <Card.Body>
                    <Card.Text>
                        <div className='row'>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="project">
                                    <Form.Label>Project<span className='required'></span></Form.Label>
                                    <Select
                                        value={project}
                                        onChange={handleProjectChange}
                                        options={projects}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        isClearable={true}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="productCategory">
                                    <Form.Label>Product Category</Form.Label>
                                    <Select
                                        value={productCategory}
                                        onChange={handleProductCategoryChange}
                                        options={productCategories}
                                        placeholder={productCategory.label}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="fromDate">
                                    <Form.Label>From Date<span className='required'></span></Form.Label>
                                    <Form.Control
                                        type="date"
                                        placeholder="From Date"
                                        name='fromDate'
                                        value={formData.fromDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="toDate">
                                    <Form.Label>To Date<span className='required'></span></Form.Label>
                                    <Form.Control
                                        type="date"
                                        placeholder="To Date"
                                        name='toDate'
                                        value={formData.toDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="showZeroQuantity">
                                    <Form.Label className='d-block'>&nbsp;</Form.Label>
                                    <div className="form-check mt-2">
                                        <Form.Check
                                            type="checkbox"
                                            id="showZeroQuantity"
                                            name="showZeroQuantity"
                                            label="Zero Quantity"
                                            checked={formData.showZeroQuantity}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </Form.Group>
                            </div>
                            <div className='col-md-1'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <button className='btn btn-sm btn-primary w-100 p-3' onClick={handleViewReport} disabled={loading}>
                                        {loading && (
                                            <span className="spinner-border spinner-border-sm"></span>
                                        )}
                                        <span>View Report</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Card.Text>
                </Card.Body>
            </Card>

            <div className='row'>
                <div className='col-12'>
                    <div style={{
                        maxHeight: 'calc(100vh - 300px)',
                        overflowY: 'auto',
                        overflowX: 'auto',
                        position: 'relative',
                    }}>
                        <Table bordered hover style={{ marginBottom: 0, width: '100%' }}>
                            <thead>
                                <tr>
                                    <th className='text-center' rowSpan={2} style={stickyGroupHeaderStyle}>Serial NO</th>
                                    <th className='text-center' rowSpan={2} style={stickyGroupHeaderStyle}>Product Name</th>
                                    <th className='text-center' rowSpan={2} style={stickyGroupHeaderStyle}>Pack Size</th>
                                    {reportData.warehouses.map((warehouse) => (
                                        <th
                                            key={warehouse.id_warehouses}
                                            className='text-center'
                                            colSpan={warehouseColumnCount}
                                            style={stickyGroupHeaderStyle}
                                        >
                                            {warehouse.name}
                                        </th>
                                    ))}
                                    <th className='text-center' rowSpan={2} style={totalClosingStockHeaderStyle}>Closing Stock</th>
                                </tr>
                                <tr>
                                    {reportData.warehouses.map((warehouse) => (
                                        <React.Fragment key={`sub-${warehouse.id_warehouses}`}>
                                            <th className='text-center' style={stockInHeaderStyle}>Opening Stock</th>
                                            <th className='text-center' style={stockInHeaderStyle}>Production</th>
                                            <th className='text-center' style={stockInHeaderStyle}>Return</th>
                                            <th className='text-center' style={stockInHeaderStyle}>Warehouse Transfer IN</th>
                                            <th className='text-center' style={stockOutHeaderStyle}>Shipment</th>
                                            <th className='text-center' style={stockOutHeaderStyle}>Warehouse Transfer OUT</th>
                                            <th className='text-center' style={closingStockHeaderStyle}>Closing Stock</th>
                                        </React.Fragment>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {!hasReportData ? (
                                    <tr>
                                        <td colSpan={totalColumns || 3} className='text-center'>No data available</td>
                                    </tr>
                                ) : (
                                    reportData.categories.map((category) => (
                                        <React.Fragment key={category.id_product_categories}>
                                            <tr className='table-secondary'>
                                                <td colSpan={totalColumns} className='fw-bold'>
                                                    {category.category_name}
                                                </td>
                                            </tr>
                                            {category.products.map((product) => renderProductRow(product))}
                                        </React.Fragment>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </div>
        </>
    );
};

export default WarehouseStockReport;
